import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useGetDealerAddressByDealerIdQuery } from '../../api/endpoints/dealeraddress';

/**
 * Dealer address dropdown component
 * @param {Object} props
 * @param {string|Object} props.value - The selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.dealerId - The dealer ID to fetch addresses for
 */
const DealerAddressDropdown = React.memo(({ value, onChange, className = '', disabled = false, placeholder = 'Bayi adresi seçiniz', dealerId }) => {
    const [selectedAddress, setSelectedAddress] = useState(null);
    const prevDealerIdRef = useRef(dealerId);

    // Fetch dealer addresses from API
    const { data: addressResponse, isLoading: isLoadingAddresses } = useGetDealerAddressByDealerIdQuery(dealerId, {
        skip: !dealerId || dealerId === 0
    });

    const addressOptions = useMemo(() => {
        if (!addressResponse?.items) return [];
        
        return addressResponse.items
            .filter(address => !dealerId || address.dealerId === dealerId || address.erpDealerId === dealerId)
            .map((address) => ({
                id: address.addrId,
                address: address.address,
                country: address.country,
                addressName: address.addressName,
                displayValue: address.addressName,
                dealerId: address.dealerId || address.erpDealerId
            }));
    }, [addressResponse, dealerId]);

    const handleSelectionChange = (e) => {
        setSelectedAddress(e.value);
        onChange(e.value || null);
    };

    useEffect(() => {
        if (value && addressOptions.length > 0) {
            const selectedId = typeof value === 'object' ? value.id : value;
            const found = addressOptions.find(opt => opt.id === selectedId);
            if (found && (!selectedAddress || selectedAddress.id !== found.id)) {
                setSelectedAddress(found);
            }
        } else if (!value) {
            setSelectedAddress(null);
        }
    }, [value, addressOptions, selectedAddress]);

    useEffect(() => {
        const dealerChanged = prevDealerIdRef.current !== dealerId;
        
        if (dealerChanged) {
            prevDealerIdRef.current = dealerId;
            setSelectedAddress(null);
            onChange(null); // Clear the value when dealer changes
        }
    }, [dealerId, onChange]);

    return (
        <Dropdown
            value={selectedAddress}
            options={addressOptions}
            onChange={handleSelectionChange}
            optionLabel="displayValue"
            showClear
            className={className}
            placeholder={placeholder}
            loading={isLoadingAddresses}
            disabled={disabled || !dealerId}
            virtualScrollerOptions={{ itemSize: 38 }}
            itemTemplate={(option) => (
                <div>
                    <b>{option.id}</b> - <span>{option.addressName}</span> - <span style={{ color: '#666' }}>{option.country}</span>
                </div>
            )}
        />
    );
});

DealerAddressDropdown.displayName = 'DealerAddressDropdown';

export default DealerAddressDropdown;
